import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Check, ChevronDown, ChevronRight, Search, User, Users } from "lucide-react";

import type { OrgScopeFilter } from "@/shared/hooks/useOrgScopeFilter";
import { orgService } from "@/entities/org-department/api/orgService";
import {
  buildDeptTree,
  searchDepartments,
  searchMembers,
  type DeptTreeNode,
} from "@/entities/org-department/lib/orgTree";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

export interface OrgScopeFilterBarProps {
  /** 由 `useOrgScopeFilter` 返回;组件按维度自持部门/成员级联查询。 */
  filter: OrgScopeFilter;
  className?: string;
}

/** 单行(全部/只看我的/搜索命中)。 */
function Row({
  active,
  onClick,
  icon,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted",
        active && "bg-primary/10 text-primary"
      )}
    >
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {active && <Check className="h-3.5 w-3.5 shrink-0" />}
    </button>
  );
}

/** 递归部门节点:chevron 展开子部门 + 懒加载成员;点部门名 = 选定该部门。 */
function DeptNode({
  node,
  depth,
  filter,
  onDone,
}: {
  node: DeptTreeNode;
  depth: number;
  filter: OrgScopeFilter;
  onDone: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const membersQ = useQuery({
    queryKey: ["org", "members", node.id],
    queryFn: () => orgService.listMembers(node.id),
    enabled: expanded,
  });
  const deptActive = filter.scope === "department" && filter.departmentId === String(node.id);
  const pad = { paddingLeft: `${depth * 14 + 4}px` };

  return (
    <div>
      <div className="flex items-center gap-0.5" style={pad}>
        <button
          type="button"
          aria-label={expanded ? "收起" : "展开"}
          onClick={() => setExpanded((v) => !v)}
          className="flex h-6 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <ChevronRight
            className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-90")}
          />
        </button>
        <button
          type="button"
          onClick={() => {
            filter.selectDepartment(String(node.id));
            onDone();
          }}
          className={cn(
            "flex flex-1 items-center gap-2 rounded px-1.5 py-1.5 text-left text-sm hover:bg-muted",
            deptActive && "bg-primary/10 text-primary"
          )}
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">{node.name}</span>
          {deptActive && <Check className="h-3.5 w-3.5 shrink-0" />}
        </button>
      </div>
      {expanded && (
        <div>
          {node.children.map((c) => (
            <DeptNode key={c.id} node={c} depth={depth + 1} filter={filter} onDone={onDone} />
          ))}
          {membersQ.isLoading && (
            <div
              className="py-1 text-xs text-muted-foreground"
              style={{ paddingLeft: `${(depth + 1) * 14 + 26}px` }}
            >
              加载成员…
            </div>
          )}
          {(membersQ.data ?? []).map((m) => {
            // 选中态需 dept+member 双匹配:同一 principal 可挂多个部门,只匹配 memberId 会在
            // 展开另一含该成员的部门时误显对勾(selectMember 存的就是 dept+member 二元组)。codex B P2。
            const memberActive =
              filter.scope === "member" &&
              filter.departmentId === String(node.id) &&
              filter.memberId === String(m.principalId);
            return (
              <button
                key={m.principalId}
                type="button"
                onClick={() => {
                  filter.selectMember(String(node.id), String(m.principalId));
                  onDone();
                }}
                style={{ paddingLeft: `${(depth + 1) * 14 + 8}px` }}
                className={cn(
                  "flex w-full items-center gap-2 rounded py-1.5 pr-2 text-left text-sm hover:bg-muted",
                  memberActive && "bg-primary/10 text-primary"
                )}
              >
                <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{m.displayName}</span>
                {memberActive && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
          {expanded &&
            !membersQ.isLoading &&
            (membersQ.data?.length ?? 0) === 0 &&
            node.children.length === 0 && (
              <div
                className="py-1 text-xs text-muted-foreground/60"
                style={{ paddingLeft: `${(depth + 1) * 14 + 26}px` }}
              >
                无子部门 / 成员
              </div>
            )}
        </div>
      )}
    </div>
  );
}

/**
 * 组织级归属筛选栏(共享,B 重设计)。仅 MANAGER+(`filter.canSwitchScope`)渲染;STAFF 返回 null(后端强制隔离)。
 *
 * <p>Radix Popover + 自建部门树 + 前端搜索(零新依赖,替代旧三 native `<select>` 伪树)。触发按钮显当前归属;
 * 展开后:搜索框 + 快捷「全部 / 只看我的」+ 可折叠部门树(点部门选定,展开懒加载成员点选成员)。搜索时扁平列出
 * 命中部门(带祖先路径消歧)。原子 selector 一步定 scope+归属。后端 `resolveOwnerFilter` 钳制在授权作用域内。
 */
export function OrgScopeFilterBar({ filter, className }: OrgScopeFilterBarProps) {
  const { scope, departmentId, memberId, canSwitchScope } = filter;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const deptsQ = useQuery({
    queryKey: ["org", "departments"],
    queryFn: () => orgService.listDepartments(),
    enabled: canSwitchScope,
  });
  const depts = deptsQ.data ?? [];

  // 2026-07-13 人名搜索:开始输入才拉全员平铺(一次,60s 内复用),与部门命中同屏分组展示。
  const allMembersQ = useQuery({
    queryKey: ["org", "all-members"],
    queryFn: () => orgService.listAllMembers(),
    enabled: canSwitchScope && query.trim().length > 0,
    staleTime: 60_000,
  });
  const selectedDept = departmentId ? depts.find((d) => d.id === Number(departmentId)) : undefined;

  // scope=member 时拉所选部门成员以解析成员名做触发按钮 label。
  const memberLabelQ = useQuery({
    queryKey: ["org", "members", departmentId],
    queryFn: () => orgService.listMembers(Number(departmentId)),
    enabled: canSwitchScope && scope === "member" && !!departmentId,
  });
  const selectedMember = memberId
    ? (memberLabelQ.data ?? []).find((m) => m.principalId === Number(memberId))
    : undefined;

  if (!canSwitchScope) return null;

  const label =
    scope === "all"
      ? "全部"
      : scope === "mine"
        ? "只看我的"
        : scope === "member"
          ? (selectedMember?.displayName ?? "指定成员")
          : (selectedDept?.name ?? "指定部门");

  const tree = buildDeptTree(depts);
  const matches = searchDepartments(depts, query);
  const memberMatches = searchMembers(allMembersQ.data ?? [], depts, query);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={className ?? "flex items-center"}>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-2" aria-label="组织归属筛选">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="max-w-[9rem] truncate">{label}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <div className="flex items-center gap-2 border-b px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索部门 / 成员…"
              className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {query ? (
              matches.length > 0 || memberMatches.length > 0 || allMembersQ.isLoading ? (
                <>
                  {matches.map((m) => (
                    <Row
                      key={`d-${m.id}`}
                      active={scope === "department" && departmentId === String(m.id)}
                      icon={<Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      onClick={() => {
                        filter.selectDepartment(String(m.id));
                        close();
                      }}
                    >
                      {m.label}
                    </Row>
                  ))}
                  {matches.length > 0 && memberMatches.length > 0 && (
                    <div className="my-1 border-t" />
                  )}
                  {/* 成员命中:同人多归属各一行(选中=该部门下的他),部门名后缀消歧 */}
                  {memberMatches.map((m) => (
                    <Row
                      key={`m-${m.departmentId}-${m.principalId}`}
                      active={
                        scope === "member" &&
                        departmentId === String(m.departmentId) &&
                        memberId === String(m.principalId)
                      }
                      icon={<User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                      onClick={() => {
                        filter.selectMember(String(m.departmentId), String(m.principalId));
                        close();
                      }}
                    >
                      {m.displayName}
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {m.departmentName}
                      </span>
                    </Row>
                  ))}
                  {allMembersQ.isLoading && (
                    <div className="py-2 text-center text-xs text-muted-foreground">搜索成员…</div>
                  )}
                </>
              ) : (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  无匹配部门或成员
                </div>
              )
            ) : (
              <>
                <Row
                  active={scope === "all"}
                  onClick={() => {
                    filter.selectAll();
                    close();
                  }}
                >
                  全部
                </Row>
                <Row
                  active={scope === "mine"}
                  onClick={() => {
                    filter.selectMine();
                    close();
                  }}
                >
                  只看我的
                </Row>
                <div className="my-1 border-t" />
                {deptsQ.isLoading ? (
                  <div className="py-4 text-center text-xs text-muted-foreground">加载部门…</div>
                ) : tree.length === 0 ? (
                  <div className="py-4 text-center text-xs text-muted-foreground">暂无部门</div>
                ) : (
                  tree.map((node) => (
                    <DeptNode key={node.id} node={node} depth={0} filter={filter} onDone={close} />
                  ))
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
